/**
 * Guard anti-duplicato per axios (coalescing delle richieste identiche in volo).
 *
 * Scopo: evitare il doppio inserimento causato dal doppio click sui pulsanti di submit.
 *
 * Come funziona: quando una richiesta "mutante" (POST/PUT/PATCH/DELETE) parte mentre ne e' gia'
 * in volo una IDENTICA (stesso metodo + url + corpo), NON ne viene inviata una seconda: la seconda
 * chiamata riceve la STESSA Promise della prima. Cosi' il doppio click produce una sola richiesta
 * di rete (un solo inserimento) e nessun errore: entrambi i click si risolvono con la stessa risposta.
 *
 * IMPORTANTE: in questo backend POST/PUT sono usati anche per le LETTURE (liste, ricerche...).
 * Per questo NON annulliamo mai le richieste (romperebbe le letture): facciamo solo coalescing,
 * che e' sicuro anche per le letture perche' unisce solo richieste contemporaneamente in volo e
 * libera la firma non appena la richiesta si conclude (nessun rischio di dati "stale").
 *
 * Installato una sola volta su `axios` in src/main.js -> copre tutte le chiamate senza modificarle.
 */

const MUTATING = ['post', 'put', 'patch', 'delete'];

// JSON.stringify con chiavi ordinate: due payload uguali producono la stessa stringa.
function stableStringify(value) {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value);
	}
	if (Array.isArray(value)) {
		return '[' + value.map(stableStringify).join(',') + ']';
	}
	const keys = Object.keys(value).sort();
	return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',') + '}';
}

function isFormData(data) {
	return typeof FormData !== 'undefined' && data instanceof FormData;
}

function bodyToString(data) {
	if (data === undefined || data === null) return 'null';
	if (typeof data === 'string') return data;
	// I FormData (upload immagini/multimedia) non sono serializzabili in modo stabile.
	if (isFormData(data)) return null;
	try {
		return stableStringify(data);
	} catch (e) {
		return null;
	}
}

export function installAxiosGuard(axios) {
	const inFlight = new Map();

	MUTATING.forEach(method => {
		const original = axios[method].bind(axios);

		axios[method] = function (url, ...rest) {
			// Firme axios: post/put/patch -> (url, data, config); delete -> (url, config)
			const data = method === 'delete' ? (rest[0] && rest[0].data) : rest[0];
			const body = bodyToString(data);

			// Niente coalescing per FormData o body non serializzabile.
			const signature = body === null ? null : method + '|' + url + '|' + body;

			if (signature && inFlight.has(signature)) {
				// Richiesta identica gia' in volo: riusa la stessa Promise (nessuna seconda chiamata).
				return inFlight.get(signature);
			}

			const promise = original(url, ...rest);

			if (signature) {
				inFlight.set(signature, promise);
				const release = () => inFlight.delete(signature);
				promise.then(release, release);
			}

			return promise;
		};
	});
}

export default installAxiosGuard;
